/**
 * DocPagesViewer — постраничный просмотрщик документа (PDF и Office) для
 * режима «Посмотреть». Все страницы одной лентой, как в Telegram/Я.Диске.
 *
 * Не зависит от встроенных PDF-плагинов браузера: iOS в iframe показывает
 * только первую страницу, Android Chrome — ничего. Страницы рендерит сервер
 * (`/pdf-page/N`, JPEG 1600px, для Office — после конвертации в PDF).
 *
 * Страницы монтируются лениво (IntersectionObserver с запасом ~1.5 экрана),
 * зум — кнопками и щипком (двумя пальцами).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";


type PdfInfo = { page_count: number };
type PageState = "idle" | "loaded" | "error";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const touchDistance = (t: TouchList) =>
  Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);


export function DocPagesViewer({
  attachmentId,
  filename,
  downloadUrl,
}: {
  attachmentId: number;
  filename: string;
  downloadUrl: string;
}) {
  const apiBase = ((import.meta.env.VITE_API_BASE as string | undefined) ?? "/api").replace(/\/$/, "");
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [current, setCurrent] = useState(1);
  // Страницы, которым уже можно грузить картинку (близко к вьюпорту).
  const [mounted, setMounted] = useState<Set<number>>(() => new Set([1, 2]));
  const [pageState, setPageState] = useState<Record<number, PageState>>({});
  // Счётчик повторов: меняет key/src у <img>, чтобы браузер перезапросил.
  const [retries, setRetries] = useState<Record<number, number>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);

  const pages = useMemo(
    () => (pageCount ? Array.from({ length: pageCount }, (_, i) => i + 1) : []),
    [pageCount],
  );

  // Метаданные: число страниц (для Office сервер тут же конвертирует в PDF).
  useEffect(() => {
    const controller = new AbortController();
    setPageCount(null);
    setInfoError(null);
    setZoom(1);
    setCurrent(1);
    setMounted(new Set([1, 2]));
    setPageState({});
    setRetries({});
    void (async () => {
      try {
        const res = await fetch(
          `${apiBase}/chat/attachments/${attachmentId}/pdf-info`,
          { credentials: "include", signal: controller.signal },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const info = await res.json() as PdfInfo;
        if (!Number.isInteger(info.page_count) || info.page_count < 1) {
          throw new Error("Некорректная структура документа");
        }
        setPageCount(info.page_count);
      } catch (e: unknown) {
        if (controller.signal.aborted) return;
        setInfoError(e instanceof Error ? e.message : "Не удалось открыть документ");
      }
    })();
    return () => controller.abort();
  }, [apiBase, attachmentId]);

  // Ленивое монтирование картинок: страница попала в зону ±150% экрана →
  // грузим. Обратно не размонтируем — картинки лёгкие, а прыжки высоты хуже.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !pageCount) return;
    const io = new IntersectionObserver((entries) => {
      const hit = entries
        .filter(e => e.isIntersecting)
        .map(e => Number((e.target as HTMLElement).dataset.page))
        .filter(n => Number.isInteger(n) && n > 0);
      if (hit.length === 0) return;
      setMounted(prev => {
        if (hit.every(n => prev.has(n))) return prev;
        const next = new Set(prev);
        hit.forEach(n => next.add(n));
        return next;
      });
    }, { root, rootMargin: "150% 0px 150% 0px" });
    pageRefs.current.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [pageCount]);

  // Индикатор «N из M»: последняя страница, чей верх выше середины вьюпорта.
  const onScroll = useCallback(() => {
    const root = scrollRef.current;
    if (!root) return;
    const mid = root.scrollTop + root.clientHeight / 2;
    let best = 1;
    pageRefs.current.forEach((el, n) => {
      if (el.offsetTop <= mid && n > best) best = n;
    });
    setCurrent(prev => (prev === best ? prev : best));
  }, []);

  // Щипок: нативный listener с passive:false — React вешает touch-события
  // пассивными, preventDefault там не работает, и браузер зумит всю страницу.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchRef.current = { dist: touchDistance(e.touches), zoom };
      }
    };
    const onMove = (e: TouchEvent) => {
      const p = pinchRef.current;
      if (!p || e.touches.length !== 2) return;
      e.preventDefault();
      const next = clampZoom(p.zoom * (touchDistance(e.touches) / p.dist));
      setZoom(prev => (Math.abs(prev - next) < 0.01 ? prev : next));
    };
    const onEnd = () => { pinchRef.current = null; };
    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("touchmove", onMove, { passive: false });
    root.addEventListener("touchend", onEnd);
    root.addEventListener("touchcancel", onEnd);
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
      root.removeEventListener("touchend", onEnd);
      root.removeEventListener("touchcancel", onEnd);
    };
  }, [zoom]);

  const pageUrl = (n: number) =>
    `${apiBase}/chat/attachments/${attachmentId}/pdf-page/${n}${retries[n] ? `?r=${retries[n]}` : ""}`;

  const setRef = (n: number) => (el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(n, el); else pageRefs.current.delete(n);
  };

  if (infoError) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 28, textAlign: "center" }}>
        <div>
          <div style={{ color: "var(--brick)", marginBottom: 14 }}>Не удалось показать документ: {infoError}</div>
          <a className="btn btn-sm btn-secondary" href={downloadUrl} download={filename}>
            <Icon name="download" size={14}/> Скачать файл
          </a>
        </div>
      </div>
    );
  }

  if (pageCount == null) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center" }}>
        <Spinner size="md" label="Подготавливаем страницы…"/>
      </div>
    );
  }

  const zoomPct = Math.round(zoom * 100);

  return (
    <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", background: "#dfe1e5" }}>
      <div style={{
        minHeight: 44,
        padding: "6px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        background: "#fff",
        borderBottom: "1px solid #dadce0",
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 13, color: "#3c4043", whiteSpace: "nowrap" }}>
          {current} из {pageCount}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={zoom <= MIN_ZOOM}
            onClick={() => setZoom(z => clampZoom(z - 0.5))}
            aria-label="Уменьшить"
            style={{ minWidth: 36 }}
          >
            −
          </button>
          <span style={{ width: 42, textAlign: "center", fontSize: 12, color: "#5f6368" }}>
            {zoomPct}%
          </span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={zoom >= MAX_ZOOM}
            onClick={() => setZoom(z => clampZoom(z + 0.5))}
            aria-label="Увеличить"
            style={{ minWidth: 36 }}
          >
            +
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="doc-pages-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: 8,
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
        }}
      >
        {pages.map((n) => {
          const state = pageState[n] ?? "idle";
          const show = mounted.has(n);
          return (
            <div
              key={n}
              ref={setRef(n)}
              data-page={n}
              style={{
                width: zoom === 1 ? "calc(100% - 2px)" : `${zoom * 100}%`,
                maxWidth: zoom === 1 ? 1000 : "none",
                margin: "0 auto 10px",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,.2)",
                position: "relative",
                // Пока картинки нет — держим место под портретный лист,
                // чтобы лента не прыгала и observer видел реальные позиции.
                aspectRatio: state === "loaded" ? undefined : "210 / 297",
              }}
            >
              {show && state !== "error" && (
                <img
                  key={`${n}-${retries[n] ?? 0}`}
                  src={pageUrl(n)}
                  alt={`${filename}, страница ${n}`}
                  draggable={false}
                  onLoad={() => setPageState(s => ({ ...s, [n]: "loaded" }))}
                  onError={() => setPageState(s => ({ ...s, [n]: "error" }))}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    opacity: state === "loaded" ? 1 : 0,
                    transition: "opacity .15s",
                  }}
                />
              )}
              {show && state === "idle" && (
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                  <Spinner size="sm" label={`Страница ${n}…`}/>
                </div>
              )}
              {state === "error" && (
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--brick)", textAlign: "center", padding: 12 }}>
                  <div>
                    Не удалось загрузить страницу {n}<br/>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ marginTop: 10 }}
                      onClick={() => {
                        setPageState(s => ({ ...s, [n]: "idle" }));
                        setRetries(r => ({ ...r, [n]: (r[n] ?? 0) + 1 }));
                      }}
                    >
                      Повторить
                    </button>
                  </div>
                </div>
              )}
              {!show && (
                <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 11, color: "#9aa0a6" }}>{n}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
